import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { ServerSelection } from '../components/proxies/ServerSelection';
import { PurchaseProxyWizard } from '../components/proxies/PurchaseProxyWizard';
import { useGetDisabledServersQuery } from '../store/api/proxiesApi';

const PROXY_SERVER_TO_KEY: Record<number, string> = {
  1: "proxy_cheap",
  2: "proxyline",
  3: "proxylte",
  4: "proxy5g",
  5: "proxygb",
};

export function ProxiesPurchasePage() {
  const { t } = useTranslation();
  const { serverId } = useParams<{ serverId?: string }>();
  const serverIdNum = serverId ? Number(serverId) : null;

  const { data: disabledData } = useGetDisabledServersQuery();
  const disabledServers: string[] = useMemo(
    () => disabledData?.data?.disabledServers || [],
    [disabledData],
  );
  const isServerDisabled = serverIdNum ? disabledServers.includes(PROXY_SERVER_TO_KEY[serverIdNum]) : false;

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 flex items-center justify-between animate-slide-up">
            <div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
                {t('proxies.purchase.title')}
              </h1>
              <p className="text-neutral-medium font-body">
                {t('proxies.purchase.subtitle')}
              </p>
            </div>
            <Link
              to="/proxies/list"
              className="px-4 py-2 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 font-body whitespace-nowrap"
            >
              {t('proxies.list.title')}
            </Link>
          </div>

          {/* Non-refundable notice for proxies - only on server selection */}
          {!serverId && (
            <div className="mb-8 card-glass rounded-xl p-4 md:p-6 border border-amber-500/30 bg-amber-500/5">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-amber-200 font-body text-sm md:text-base">
                  {t('proxies.purchase.nonRefundableNotice')}
                </p>
              </div>
            </div>
          )}

          {serverId ? (
            isServerDisabled ? (
              <div className="max-w-2xl mx-auto">
                <div className="card-glass rounded-2xl shadow-2xl p-8">
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20 mb-4">
                      <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <p className="text-xl font-heading font-semibold text-red-400 mb-2">
                      {t('temporaryNumbers.purchase.serverDisabled')}
                    </p>
                    <p className="text-neutral-medium font-body">
                      {t('temporaryNumbers.purchase.serverDisabledDesc')}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <PurchaseProxyWizard />
            )
          ) : (
            <ServerSelection />
          )}
        </div>
      </div>
    </div>
  );
}

